// SEO structured-data helpers for React Router `meta()` exports.
// Each returns a MetaDescriptor that renders a <script type="application/ld+json"> tag.

const BASE_URL = "https://www.escuelavial.cl";

/** Agrega " | Escuela Vial" solo si el título completo cabe en 70 caracteres (límite de Bing; Google corta antes). */
export function seoTitle(base: string) {
  const full = `${base} | Escuela Vial`;
  return full.length <= 70 ? full : base;
}

export type BreadcrumbItem = { name: string; path: string };

/**
 * BreadcrumbList JSON-LD. Pass the trail from the site root to the current page,
 * mirroring the visible breadcrumb. Example:
 *   breadcrumbLd([
 *     { name: "Inicio", path: "/" },
 *     { name: "Obtener licencia", path: "/obtener-licencia" },
 *     { name: "Licencia Clase B", path: "/licencia-clase-b" },
 *   ])
 */
export function breadcrumbLd(trail: BreadcrumbItem[]) {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: `${BASE_URL}${item.path}`,
      })),
    },
  } as const;
}

/**
 * Article JSON-LD para páginas de contenido educativo (resúmenes del libro).
 * Referencia al Organization del sitio (definido en root.tsx con @id #organization)
 * y a la fuente oficial (CONASET) vía isBasedOn, aportando señales de E-E-A-T.
 */
export function articleLd(opts: {
  headline: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
}) {
  const headline = opts.headline.replace(/\s*\|\s*Escuela Vial\s*$/i, "").slice(0, 110);
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "Article",
      headline,
      description: opts.description,
      inLanguage: "es-CL",
      datePublished: opts.datePublished ?? "2026-08-28",
      dateModified: opts.dateModified ?? "2026-08-28",
      mainEntityOfPage: opts.url,
      ...(opts.image && { image: opts.image }),
      author: { "@type": "Organization", name: "Escuela Vial", url: BASE_URL },
      publisher: { "@id": `${BASE_URL}/#organization` },
      isBasedOn: "https://mejoresconductores.conaset.cl",
    },
  } as const;
}

/**
 * VideoObject JSON-LD. Úsalo solo en la página donde el video es el contenido principal
 * (las páginas /videos/:slug); en los capítulos el video es complementario y no se marca.
 */
export function videoLd(opts: {
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  uploadDate: string;
  duration: string;
  transcript: string;
  url: string;
}) {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: opts.name,
      description: opts.description,
      thumbnailUrl: [opts.thumbnailUrl],
      contentUrl: opts.contentUrl,
      uploadDate: `${opts.uploadDate}T12:00:00-03:00`,
      duration: opts.duration,
      transcript: opts.transcript,
      inLanguage: "es-CL",
      mainEntityOfPage: opts.url,
      publisher: { "@id": `${BASE_URL}/#organization` },
      isBasedOn: "https://mejoresconductores.conaset.cl",
    },
  } as const;
}

/** ItemList JSON-LD para páginas índice (lista ordenada de capítulos/anexos). */
export function itemListLd(items: { name: string; path: string }[]) {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items.map((it, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: it.name,
        url: `${BASE_URL}${it.path}`,
      })),
    },
  } as const;
}

export type FaqItem = { q: string; a: string };

/** FAQPage JSON-LD. Only use on pages with genuine, visible question/answer pairs. */
export function faqLd(items: FaqItem[]) {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  } as const;
}
